import { useRef } from 'react';
import toast from 'react-hot-toast';
import AsyncRequestButton from '~/components/AsyncRequestButton';
import { exportBuyerProfiles, pollBuyerProfileExport } from '~/api/admin';
import { ApiEvent } from '~/types/ApiEvent';
import { ToastDuration } from '~/constants/toasts';

export default function DownloadBuyerProfiles() {
  // Stashed by pollFn on SUCCESS so onSuccess can trigger the download.
  const downloadUrlRef = useRef<string | null>(null);

  return (
    <AsyncRequestButton
      hideEstimatedTime
      onClick={async () => {
        try {
          downloadUrlRef.current = null;
          return await exportBuyerProfiles();
        } catch (err) {
          toast.error('An error occurred while requesting the buyer profile export. Please refresh the page and try again.');
          throw err;
        }
      }}
      pollFn={async (apiEventId: string) => {
        const result = await pollBuyerProfileExport(apiEventId);
        if (result.status === 'ERROR') {
          throw new Error(result.errorMessage ?? 'Buyer profile export failed');
        }
        if (result.status === 'SUCCESS') {
          downloadUrlRef.current = result.downloadUrl ?? null;
        }
        return { status: result.status } as ApiEvent;
      }}
      onSuccess={() => {
        if (downloadUrlRef.current) {
          window.location.href = downloadUrlRef.current;
        }
        toast.success('Buyer profile export downloaded. A copy was also emailed to you.');
      }}
      onError={() => {
        toast.error('An error occurred generating the buyer profile export. Please try again.');
      }}
      onTimeout={() => {
        toast(
          'The buyer profile export is taking longer than expected. You will receive an email with a download link when it is ready.',
          { duration: ToastDuration.LONG }
        );
      }}
      text="Export Buyer Profiles (.csv)"
      loadingText="Generating export"
    />
  );
}
